import { CandidateLocationSchema } from "@artha/api-contracts";
import type { LocationSearchProvider } from "@artha/ui";
import {
  GOOGLE_CITY_COMPONENT_TYPES,
  GOOGLE_PLACE_FIELDS,
  GOOGLE_PLACES_BASE_URL,
  GOOGLE_PLACES_TIMEOUT_MS,
} from "./googlePlaces.constants";
import type {
  GoogleAutocompleteResponse,
  GooglePlace,
} from "./googlePlaces.types";

async function requestPlaces<T>(
  path: string,
  apiKey: string | undefined,
  options: RequestInit,
): Promise<T> {
  if (!apiKey) throw new Error("Location suggestions are unavailable");
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    GOOGLE_PLACES_TIMEOUT_MS,
  );
  try {
    const response = await fetch(`${GOOGLE_PLACES_BASE_URL}/${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": apiKey,
        ...options.headers,
      },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error("Location lookup failed");
    return (await response.json()) as T;
  } finally {
    clearTimeout(timeout);
  }
}

export function createGooglePlacesProvider(
  apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
): LocationSearchProvider {
  let sessionToken: string | undefined;
  const predictions = new Map<string, string>();
  return {
    async search(query) {
      const input = query.trim();
      if (!input) return [];
      sessionToken ||= crypto.randomUUID();
      const token = sessionToken;
      const { suggestions = [] } =
        await requestPlaces<GoogleAutocompleteResponse>(
          "places:autocomplete",
          apiKey,
          {
            method: "POST",
            body: JSON.stringify({
              input,
              includedPrimaryTypes: ["(cities)"],
              sessionToken: token,
            }),
          },
        );
      return suggestions.flatMap(({ placePrediction }) => {
        if (!placePrediction) return [];
        predictions.set(placePrediction.placeId, token);
        return [
          { id: placePrediction.placeId, label: placePrediction.text.text },
        ];
      });
    },
    async resolve(id) {
      const token = predictions.get(id);
      if (!token)
        throw new Error("Please select a current location suggestion");
      sessionToken = undefined;
      predictions.clear();
      const params = new URLSearchParams({ sessionToken: token });
      const place = await requestPlaces<GooglePlace>(
        `places/${encodeURIComponent(id)}?${params}`,
        apiKey,
        {
          headers: { "X-Goog-FieldMask": GOOGLE_PLACE_FIELDS },
        },
      );
      const component = (type: string) =>
        place.addressComponents?.find((entry) => entry.types.includes(type));
      const city = GOOGLE_CITY_COMPONENT_TYPES.map(
        (type) => component(type)?.longText,
      ).find(Boolean);
      if (
        !city ||
        typeof place.location?.latitude !== "number" ||
        typeof place.location.longitude !== "number"
      )
        throw new Error("This location has no city or coordinates");
      return CandidateLocationSchema.parse({
        city,
        state: component("administrative_area_level_1")?.longText,
        country: component("country")?.shortText,
        latitude: place.location.latitude,
        longitude: place.location.longitude,
        placeId: place.id || id,
      });
    },
  };
}
